import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';

type Mode = 'signin' | 'signup';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useAuth();

  const [mode, setMode] = useState<Mode>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (session) navigate('/app', { replace: true });
  }, [session, navigate]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === 'signin') {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        navigate('/app', { replace: true });
      } else {
        const { data, error: err } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name } }
        });
        if (err) throw err;
        if (data.session) navigate('/app', { replace: true });
        else setInfo(t('auth.check_email'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/app' }
    });
    if (err) setError(err.message);
  }

  return (
    <div className="auth-card">
      <h1 className="auth-card__title">
        {mode === 'signin' ? t('auth.signin_title') : t('auth.signup_title')}
      </h1>

      <button type="button" className="btn btn--ghost btn--block" onClick={() => void handleGoogle()}>
        {t('auth.google_cta')}
      </button>

      <div className="auth-divider">{t('auth.or')}</div>

      <form onSubmit={handleSubmit} className="form">
        {mode === 'signup' && (
          <label className="field">
            <span>{t('auth.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </label>
        )}
        <label className="field">
          <span>{t('auth.email')}</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
        <label className="field">
          <span>{t('auth.password')}</span>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
          />
        </label>

        {error && <p className="form-error">{error}</p>}
        {info && <p className="form-info">{info}</p>}

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {mode === 'signin' ? t('auth.signin_cta') : t('auth.signup_cta')}
        </button>
      </form>

      <button
        type="button"
        className="link-button"
        onClick={() => {
          setMode(mode === 'signin' ? 'signup' : 'signin');
          setError(null);
          setInfo(null);
        }}
      >
        {mode === 'signin' ? t('auth.to_signup') : t('auth.to_signin')}
      </button>

      <div className="auth-back">
        <Link to="/">← {t('app.name')}</Link>
      </div>
    </div>
  );
}
